/* @flow */
import AffiliationStore from 'stores/AffiliationStore';
import CurrentUser from 'auth/CurrentUser';
import cx from 'classnames';
import FormSelect from 'components/FormSelect.jsx';
import Glyph from 'components/Glyph.jsx';
import Group from 'entities/Group';
import LayerTrigger from 'components/LayerTrigger.jsx';
import Link from 'components/Link.jsx';
import Menu from 'components/Menu.jsx';
import MenuItem from 'components/MenuItem.jsx';
import OrganizationStore from 'stores/OrganizationStore';
import React from 'react';
import styles from './styles/GroupSchoolSelect.css';
import { Container } from 'flux/utils';
import Organization from 'entities/Organization';

const UNAFFILIATED_CLASS_ID = -1;

type Props = {
  onSchoolChange?: ?(id: ?string) => void,
  initialOrganizationId?: ?number,
  group?: ?Group,
  hideUnaffiliated?: ?boolean,
  prefix?: ?string,
  input?: ?string,
}

type State = {
  memberOrganizations: Organization[],
  organization: ?Organization,
}

class GroupSchoolSelect extends React.Component {
  props: Props;
  state: State;

  static getStores() {
    return [AffiliationStore, OrganizationStore];
  }

  static calculateState(prevState: ?State, instanceProps: ?Props): State {
    const memberOrganizations = AffiliationStore.getMemberOrganizations();
    const props = instanceProps; // So Flow is ok with the lambda in `find`
    const existingOrganization = prevState && prevState.organization;
    return {
      memberOrganizations,
      organization: existingOrganization || (
        props ?
          memberOrganizations.find(o => o.id === props.initialOrganizationId) :
          null
      ),
    };
  }

  defaultProps = {
    input: 'select',
    hideUnaffiliated: false,
  }

  componentDidMount() {
    if (this.refs.school) {
      this.refs.school.setValue(this.getSelectedSchoolId());
    }
  }

  componentWillReceiveProps(nextProps) {
    const hideChanged = this.props.hideUnaffiliated !== nextProps.hideUnaffiliated;
    if (hideChanged && this.refs.school) {
      this.refs.school.setValue(this.getSelectedSchoolId());
    }
  }

  unaffiliatedIsInvalid(): boolean {
    return (
      this.props.hideUnaffiliated ||
      (!!this.props.group && this.props.group.hasChildren())
    );
  }

  onSchoolChange = (orgId: string) => {
    this.setState({ organization: this.state.memberOrganizations.find(o => o.id.toString() === orgId) });
    const { onSchoolChange } = this.props;
    if (onSchoolChange) {
      onSchoolChange(
        orgId === UNAFFILIATED_CLASS_ID.toString() ? null : orgId
      );
    }
  }

  onSelect = (orgId: string) => {
    this.onSchoolChange(orgId);
  }

  getSchoolSelectOptions() {
    const organizationOptions = this.state.memberOrganizations.map(org => {
      return { value: org.id, label: org.getName() };
    });

    if (!this.unaffiliatedIsInvalid() || this.preExistingUnaffiliatedU13()) {
      organizationOptions.push({ value: UNAFFILIATED_CLASS_ID, label: 'Not affiliated with a school' });
    }
    return organizationOptions;
  }

  getSelectedSchoolName(): string {
    const name = this.state.organization ?
      this.state.organization.getName() : 'Not affiliated with a school';
    return name;
  }

  getSelectedSchoolId(): number {
    return this.state.organization ?
      this.state.organization.id :
      UNAFFILIATED_CLASS_ID;
  }

  getOrganizationForSubmit(): ?Organization {
    return this.state.organization;
  }

  // typecheck: ignore statement
  shouldShowSchool(): boolean {
    return CurrentUser.getUser().isOrganizationPickerEligible();
  }

  preExistingUnaffiliatedU13(): boolean {
    return (
      !this.state.organization &&
      this.unaffiliatedIsInvalid()
    );
  }

  validation() {
    return (
      this.unaffiliatedIsInvalid() &&
      'isGreaterThan: -1'
    );
  }

  renderMenu() {
    const menu = (
      <Menu
        className={cx({
          [styles.schoolMenu]: true,
          'GroupForm-schoolMenu': true,
          [styles.hasUnaffiliated]: !this.unaffiliatedIsInvalid(),
          'GroupForm-hasUnaffiliated': !this.unaffiliatedIsInvalid(),
        })}
        selectedKey={this.getSelectedSchoolId().toString()}>
        {
          this.getSchoolSelectOptions().map(option =>
            <MenuItem
              key={option.value}
              onSelect={this.onSelect}>
              {option.label}
            </MenuItem>
          )
        }
      </Menu>
    );

    return (
      <div>
        {this.state.organization && this.props.prefix}
        <LayerTrigger
          layer={menu}
          trigger="click"
          placement="bottom"
          alignment="middle">
          <Link href="#">
            <span>
              {this.getSelectedSchoolName()}
              <Glyph name="chevron-down" size="xsmall" />
            </span>
          </Link>
        </LayerTrigger>
      </div>
    );
  }

  renderSelect() {
    return (
      <FormSelect
        label="School"
        name="school"
        ref="school"
        id="schoolSelect"
        searchable={false}
        options={this.getSchoolSelectOptions()}
        value={this.getSelectedSchoolId()}
        onChange={this.onSchoolChange}
        validations={this.validation()}
        validationError="The class must be connected to a school if it contains students under 13."
        className={cx({
          [styles.schoolSelect]: true,
          'GroupForm-schoolSelect': true,
          [styles.hasUnaffiliated]: !this.unaffiliatedIsInvalid(),
          'GroupForm-hasUnaffiliated': !this.unaffiliatedIsInvalid(),
        })}
      />
    );
  }

  render() {
    if (!this.shouldShowSchool()) {
      return null;
    }

    return this.props.input === 'menu' ? this.renderMenu() : this.renderSelect();
  }
}

export default Container.create(GroupSchoolSelect, { withProps: true });



// WEBPACK FOOTER //
// ./src/groups/components/GroupSchoolSelect.jsx